import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { coreAPI } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import { useToast } from '../../context/ToastContext';
import Pagination from '../../components/Pagination';
import './Admin.css';

const POSITION_LABELS = {
  chairman: '主席',
  vice_chairman: '副主席',
  secretary: '秘書',
  treasurer: '司庫',
  director: '理事',
  advisor: '顧問',
  member: '委員',
  other: '其他',
};

const OrganizationMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { success, error } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await coreAPI.adminGetOrganizationMembers();
      setMembers(Array.isArray(response.data) ? response.data : response.data?.results || []);
    } catch (err) {
      error('無法載入組織成員');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (memberId, memberName) => {
    if (!window.confirm(`確定要刪除「${memberName}」？`)) return;
    try {
      await coreAPI.adminDeleteOrganizationMember(memberId);
      success('成員已刪除');
      fetchMembers();
    } catch (err) {
      error('刪除失敗');
    }
  };

  const handleToggleActive = async (member) => {
    try {
      await coreAPI.adminPatchOrganizationMember(member.id, { is_active: !member.is_active });
      success(member.is_active ? '已停用' : '已啟用');
      fetchMembers();
    } catch (err) {
      error('更新失敗');
    }
  };

  const filteredMembers = members.filter((member) =>
    !search ||
    member.name?.toLowerCase().includes(search.toLowerCase()) ||
    member.position_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMembers = filteredMembers.slice(startIndex, startIndex + itemsPerPage);

  if (loading) return <LoadingPage message="載入組織成員中..." />;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>組織架構管理</h1>
        <div className="admin-actions">
          <input
            type="text"
            placeholder="搜尋姓名、職位..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <button className="btn btn-primary" onClick={() => navigate('/admin/organization-members/new')}>
            新增成員
          </button>
        </div>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>姓名</th>
              <th>職位</th>
              <th>順序</th>
              <th>狀態</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {paginatedMembers.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>
                  沒有組織成員
                </td>
              </tr>
            ) : (
              paginatedMembers.map((member) => (
                <tr key={member.id}>
                  <td><strong>{member.name}</strong></td>
                  <td>{member.position_title || POSITION_LABELS[member.position] || member.position}</td>
                  <td>{member.display_order}</td>
                  <td>
                    <span className={`status-badge ${member.is_active ? 'status-published' : 'status-draft'}`}>
                      {member.is_active ? '啟用' : '停用'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleToggleActive(member)}
                        className="btn btn-sm btn-outline"
                      >
                        {member.is_active ? '停用' : '啟用'}
                      </button>
                      <button
                        onClick={() => navigate(`/admin/organization-members/${member.id}/edit`)}
                        className="btn btn-sm btn-primary"
                      >
                        編輯
                      </button>
                      <button
                        onClick={() => handleDelete(member.id, member.name)}
                        className="btn btn-sm btn-danger"
                      >
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={setItemsPerPage}
        totalItems={filteredMembers.length}
      />
    </div>
  );
};

export default OrganizationMembers;
